import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import VisitorsPage from "./pages/VisitorsPage";
import VisitorDetailPage from "./pages/VisitorDetailPage";
import MembersPage from "./pages/MembersPage";
import MemberDetailPage from "./pages/MemberDetailPage";
import PowerTeamsPage from "./pages/PowerTeamsPage";
import CategoriesPage from "./pages/CategoriesPage";
import FollowUpsPage from "./pages/FollowUpsPage";
import ImportPage from "./pages/ImportPage";
import SettingsPage from "./pages/SettingsPage";
import EventsPage from "./pages/EventsPage";
import UsersPage from "./pages/UsersPage";
import AuditLogPage from "./pages/AuditLogPage";
import ReportsPage from "./pages/ReportsPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chapter, setChapter] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        if (userData.chapter_id) {
          fetchChapter(userData.chapter_id, token);
        }
      } else {
        localStorage.removeItem("token");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("token");
    }
    setLoading(false);
  }, []);

  const fetchChapter = async (chapterId, token) => {
    try {
      const response = await fetch(`${API}/chapters/${chapterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const chapterData = await response.json();
        setChapter(chapterData);
      }
    } catch (error) {
      console.error("Chapter fetch failed:", error);
    }
  };

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    if (userData.chapter_id) {
      fetchChapter(userData.chapter_id, token);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setChapter(null);
  };

  const updateChapter = (chapterData) => {
    setChapter(chapterData);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, chapter, loading, login, logout, updateChapter, updateUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await fetch(`${API}/auth/session?session_id=${sessionId}`);
          if (response.ok) {
            const data = await response.json();
            await login(data.access_token, data.user);
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/login", { replace: true });
          }
        } catch (error) {
          console.error("Session processing failed:", error);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processSession();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/visitors" element={
        <ProtectedRoute>
          <VisitorsPage />
        </ProtectedRoute>
      } />
      <Route path="/visitors/:id" element={
        <ProtectedRoute>
          <VisitorDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/members" element={
        <ProtectedRoute>
          <MembersPage />
        </ProtectedRoute>
      } />
      <Route path="/members/:id" element={
        <ProtectedRoute>
          <MemberDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/power-teams" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <PowerTeamsPage />
        </ProtectedRoute>
      } />
      <Route path="/categories" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <CategoriesPage />
        </ProtectedRoute>
      } />
      <Route path="/followups" element={
        <ProtectedRoute>
          <FollowUpsPage />
        </ProtectedRoute>
      } />
      <Route path="/import" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <ImportPage />
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute>
          <EventsPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/audit-log" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <AuditLogPage />
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute allowedRoles={["admin"]}>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
